import api from '@pray/shared/utils/api';

export type SuperFunnelBlocker = { code: string; message: string };

export type SuperFunnelPreflight = {
  leader: {
    artistId: string;
    name: string;
    ministryName: string;
    handle: string;
    profileUrl: string;
    profileImageUrl: string;
    description: string;
  };
  resources: {
    senderProfiles: Array<{
      id: string;
      name: string;
      emailUsername: string;
      domain: string;
      replyToEmail: string | null;
      isDefault: boolean;
    }>;
    selectedSenderProfileId: string | null;
    unsubscribeGroups: Array<{
      id: number;
      name: string;
      isDefault: boolean;
      isArtistDefault: boolean;
    }>;
    selectedUnsubscribeGroupId: number | null;
    leadClients: Array<{ id: string; name: string }>;
    selectedLeadClientId: string | null;
    funds: Array<{ id: string; name: string; isPrimaryDonation: boolean }>;
    selectedFundId: string | null;
  };
  existing: {
    workflow: { id: string; name: string; status: string } | null;
    leadCampaigns: Array<{ id: string; name: string; currentCount: number }>;
  };
  sources: Array<{ kind: 'pray_profile' | 'official_website'; label: string; url: string }>;
  blockers: SuperFunnelBlocker[];
};

export type SuperFunnelSourceKind = 'pray_profile' | 'official_website' | 'ebook';
export type SuperFunnelEmailKind =
  | 'ebook_delivery'
  | 'subscription_plus'
  | 'leader_introduction'
  | 'devotional'
  | 'one_time_gift';

export type SuperFunnelContentDraft = {
  schemaVersion: 'superfunnel.content.v1';
  sourceBrief: {
    ebookTitle: string;
    ebookSubtitle: string;
    ebookSummary: string;
    mission: { text: string; sourceKind: SuperFunnelSourceKind };
    tone: { text: string; sourceKind: SuperFunnelSourceKind };
    valuePropositions: Array<{ text: string; sourceKind: SuperFunnelSourceKind }>;
    leaderQuote: { text: string; sourceKind: SuperFunnelSourceKind };
    donationQuote: { text: string; sourceKind: SuperFunnelSourceKind };
  };
  emails: [
    SuperFunnelEmailDraft,
    SuperFunnelEmailDraft,
    SuperFunnelEmailDraft,
    SuperFunnelEmailDraft,
    SuperFunnelEmailDraft
  ];
};

export type SuperFunnelEmailDraft = {
  kind: SuperFunnelEmailKind;
  subject: string;
  preheader: string;
  headline: string;
  body: Array<{ type: 'paragraph' | 'bullets' | 'quote'; text: string; items: string[] }>;
};

type PreflightApi = {
  leader: {
    artist_id: string;
    name: string;
    ministry_name: string;
    handle: string;
    profile_url: string;
    profile_image_url: string;
    description: string;
  };
  resources: {
    sender_profiles: Array<{
      id: string;
      name: string;
      email_username: string;
      domain: string;
      reply_to_email: string | null;
      is_default: boolean;
    }>;
    selected_sender_profile_id: string | null;
    unsubscribe_groups: Array<{
      id: number;
      name: string;
      is_default: boolean;
      is_artist_default: boolean;
    }>;
    selected_unsubscribe_group_id: number | null;
    lead_clients: Array<{ id: string; name: string }>;
    selected_lead_client_id: string | null;
    funds: Array<{ id: string; name: string; is_primary_donation: boolean }>;
    selected_fund_id: string | null;
  };
  existing: {
    workflow: { id: string; name: string; status: string } | null;
    lead_campaigns: Array<{ id: string; name: string; current_count: number }>;
  };
  sources: SuperFunnelPreflight['sources'];
  blockers: SuperFunnelBlocker[];
};

type SourcedTextApi = { text: string; source_kind: SuperFunnelSourceKind };

type ContentDraftApi = {
  schema_version: 'superfunnel.content.v1';
  source_brief: {
    ebook_title: string;
    ebook_subtitle?: string;
    ebook_summary: string;
    mission: SourcedTextApi;
    tone: SourcedTextApi;
    value_propositions: SourcedTextApi[];
    leader_quote: SourcedTextApi;
    donation_quote?: SourcedTextApi;
  };
  emails: [EmailDraftApi, EmailDraftApi, EmailDraftApi, EmailDraftApi, EmailDraftApi];
};

type EmailDraftApi = {
  kind: SuperFunnelEmailKind;
  subject: string;
  preheader: string;
  headline: string;
  body: SuperFunnelEmailDraft['body'];
};

function mapPreflight(data: PreflightApi): SuperFunnelPreflight {
  return {
    leader: {
      artistId: data.leader.artist_id,
      name: data.leader.name,
      ministryName: data.leader.ministry_name,
      handle: data.leader.handle,
      profileUrl: data.leader.profile_url,
      profileImageUrl: data.leader.profile_image_url,
      description: data.leader.description,
    },
    resources: {
      senderProfiles: data.resources.sender_profiles.map((sender) => ({
        id: sender.id,
        name: sender.name,
        emailUsername: sender.email_username,
        domain: sender.domain,
        replyToEmail: sender.reply_to_email,
        isDefault: sender.is_default,
      })),
      selectedSenderProfileId: data.resources.selected_sender_profile_id,
      unsubscribeGroups: data.resources.unsubscribe_groups.map((group) => ({
        id: group.id,
        name: group.name,
        isDefault: group.is_default,
        isArtistDefault: group.is_artist_default,
      })),
      selectedUnsubscribeGroupId: data.resources.selected_unsubscribe_group_id,
      leadClients: data.resources.lead_clients,
      selectedLeadClientId: data.resources.selected_lead_client_id,
      funds: data.resources.funds.map((fund) => ({
        id: fund.id,
        name: fund.name,
        isPrimaryDonation: fund.is_primary_donation,
      })),
      selectedFundId: data.resources.selected_fund_id,
    },
    existing: {
      workflow: data.existing.workflow,
      leadCampaigns: data.existing.lead_campaigns.map((campaign) => ({
        id: campaign.id,
        name: campaign.name,
        currentCount: campaign.current_count,
      })),
    },
    sources: data.sources,
    blockers: data.blockers,
  };
}

function mapContent(data: ContentDraftApi): SuperFunnelContentDraft {
  return {
    schemaVersion: data.schema_version,
    sourceBrief: {
      ebookTitle: data.source_brief.ebook_title,
      ebookSubtitle: data.source_brief.ebook_subtitle ?? '',
      ebookSummary: data.source_brief.ebook_summary,
      mission: { text: data.source_brief.mission.text, sourceKind: data.source_brief.mission.source_kind },
      tone: { text: data.source_brief.tone.text, sourceKind: data.source_brief.tone.source_kind },
      valuePropositions: data.source_brief.value_propositions.map((value) => ({
        text: value.text,
        sourceKind: value.source_kind,
      })),
      leaderQuote: {
        text: data.source_brief.leader_quote.text,
        sourceKind: data.source_brief.leader_quote.source_kind,
      },
      donationQuote: {
        text: data.source_brief.donation_quote?.text ?? '',
        sourceKind: data.source_brief.donation_quote?.source_kind ?? 'pray_profile',
      },
    },
    emails: data.emails.map((email) => ({
      kind: email.kind,
      subject: email.subject,
      preheader: email.preheader,
      headline: email.headline,
      body: email.body,
    })) as SuperFunnelContentDraft['emails'],
  };
}

function unmapContent(data: SuperFunnelContentDraft): ContentDraftApi {
  return {
    schema_version: data.schemaVersion,
    source_brief: {
      ebook_title: data.sourceBrief.ebookTitle,
      ebook_subtitle: data.sourceBrief.ebookSubtitle,
      ebook_summary: data.sourceBrief.ebookSummary,
      mission: { text: data.sourceBrief.mission.text, source_kind: data.sourceBrief.mission.sourceKind },
      tone: { text: data.sourceBrief.tone.text, source_kind: data.sourceBrief.tone.sourceKind },
      value_propositions: data.sourceBrief.valuePropositions.map((value) => ({
        text: value.text,
        source_kind: value.sourceKind,
      })),
      leader_quote: {
        text: data.sourceBrief.leaderQuote.text,
        source_kind: data.sourceBrief.leaderQuote.sourceKind,
      },
      donation_quote: {
        text: data.sourceBrief.donationQuote.text,
        source_kind: data.sourceBrief.donationQuote.sourceKind,
      },
    },
    emails: data.emails.map((email) => ({
      kind: email.kind,
      subject: email.subject,
      preheader: email.preheader,
      headline: email.headline,
      body: email.body,
    })) as ContentDraftApi['emails'],
  };
}

export async function getSuperFunnelPreflight(artistId: string): Promise<SuperFunnelPreflight> {
  const response = await api.executeGetRequest<{ data: PreflightApi }>(
    '/web/studio/admin/superfunnel-automations/preflight',
    { artist_id: artistId }
  );
  return mapPreflight(response.data.data);
}

export async function generateSuperFunnelPreview(input: {
  artistId: string;
  ebookPdfUrl: string;
}): Promise<{ preflight: SuperFunnelPreflight; content: SuperFunnelContentDraft }> {
  const response = await api.executePostRequest<{ data: { preflight: PreflightApi; content: ContentDraftApi } }>(
    '/web/studio/admin/superfunnel-automations/preview',
    { artist_id: input.artistId, ebook_pdf_url: input.ebookPdfUrl }
  );
  return {
    preflight: mapPreflight(response.data.data.preflight),
    content: mapContent(response.data.data.content),
  };
}

export type CreateGeneratedSuperFunnelInput = {
  artistId: string;
  workflowName: string;
  senderProfileId: string;
  unsubscribeGroupId: number;
  leadClientId: string;
  fundId: string;
  offerId: string;
  ebookPdfUrl: string;
  ebookFileSize: number;
  headerImageUrl: string;
  content: SuperFunnelContentDraft;
};

export type GeneratedSuperFunnelResult = {
  offer: { id: string };
  leadCampaign: { id: string; name: string; active: false };
  workflow: {
    workflow: { id: string; name: string; status: 'draft' };
    campaigns: Array<{ id: string; name: string }>;
    draft: { id: string; draftRevision: number };
  };
};

export async function createGeneratedSuperFunnel(
  input: CreateGeneratedSuperFunnelInput
): Promise<GeneratedSuperFunnelResult> {
  const response = await api.executePostRequest<{
    data: {
      offer: { id: string };
      lead_campaign: { id: string; name: string; active: false };
      workflow: {
        workflow: { id: string; name: string; status: 'draft' };
        campaigns: Array<{ id: string; name: string }>;
        draft: { id: string; draft_revision: number };
      };
    };
  }>('/web/studio/admin/superfunnel-automations/generated', {
    artist_id: input.artistId,
    workflow_name: input.workflowName,
    sender_profile_id: input.senderProfileId,
    unsubscribe_group_id: input.unsubscribeGroupId,
    lead_client_id: input.leadClientId,
    fund_id: input.fundId,
    offer_id: input.offerId,
    ebook_pdf_url: input.ebookPdfUrl,
    ebook_file_size: input.ebookFileSize,
    header_image_url: input.headerImageUrl,
    content: unmapContent(input.content),
  });
  const { data } = response.data;
  return {
    offer: data.offer,
    leadCampaign: data.lead_campaign,
    workflow: {
      workflow: data.workflow.workflow,
      campaigns: data.workflow.campaigns,
      draft: { id: data.workflow.draft.id, draftRevision: data.workflow.draft.draft_revision },
    },
  };
}
